// The schema cache is based upon the group cache. It is also a single browser-session cache of the schema list, details and all fields in these schemas.
// It is used to speed up column derivation and selection elements. When the app starts, the App component calls initialFetch().
// Then individual components can await waitForData() to ensure that the schema cache is ready beforehand.
// If components need to be notified of changes, they can watch getCacheRef() and react to changes.

// Schemas can be one of two; regular schemas that describe a mote, or relation schemas that describe a relation between two motes.
// Relation schemas are used to describe many-to-many relations between motes, the meta.aType/bType fields are arrays that describe regular schema type's that they can link to.
// However, each relation schema automatically creates fields in the two related schemas describing the relation - with data stored in the relation's payload as well.

import { inject, ref } from 'vue';
import { Field } from './fields';

const SchemaCache = {
	_cache: ref({}),			// Cache of schema objects by id. This is a ref so it can be watched by other components.
	_fieldCache: ref({}),		// Cache of field objects by id, for sub-lookup purposes only.
	_promise: null,				// Promise that resolves when the schema cache is ready.
	$http: null,				// HTTP client to use for fetching schema list. Set the first time fetchSchemaList is called - which must be in a setup().
	
	// Called initially by App component to fetch schema from API backend, once or on manual refresh.
	fetchSchemaList() {
		if (!this.$http)
			this.$http = inject('$http');

		this._promise = new Promise(resolve => {
			// TODO: Error handling for this call!
			this.$http.get('/schema?initial=1').then(response => {
				// Load each schema object into our cache, as well as processing the fields array to instantiate Field objects.
				this._cache.value = {};
				this._fieldCache.value = {};
				for (const schema of response.data) {
					this._cache.value[schema.id] = schema;
					// For display purposes and utility, schema's have an additional keyspec and label property. The kespec should be unique and useful as an ID, even in groups of schemas+fields.
					this._cache.value[schema.id].label = schema.title;
					this._cache.value[schema.id].keyspec = schema.type;		// This is used in the Field constructor as well.
					schema.fields = schema.fields.map(field => new Field(field));
					for (const f of schema.fields) {
						this._fieldCache.value[f.id] = f;
					}
				}
				resolve();
			}).catch(error => {
				console.error('Error fetching schema list:', error);
			});
		});
	},

	// Await'ed by other components to wait for cache ready. Note that this MUST be done in a setup or later
	// lifecycle hook so App base component has a chance to call fetchSchemaList to setup the promise first!
	waitForData() {
		return this._promise;
	},

	// Get all schemas or fields in the cache, usually for use in iterators. If intending to change them, this MUST be cloned first!
	getAllSchemas(as_map = false) {
		return as_map ? this._cache.value : Object.values(this._cache.value);
	},
	getAllSchemasExceptRelations() {
		return Object.values(this._cache.value).filter(schema => !schema.is_relation);
	},
	getAllFields(as_map = false) {
		return as_map ? this._fieldCache.value : Object.values(this._fieldCache.value);
	},
	
	// Get a schema object or field object by its ID - if it is intended to be changed, it MUST be cloned first!
	getSchemaById(id) {
		return this._cache.value[id] ?? null;
	},
	getFieldById(id) {
		return this._fieldCache.value[id] ?? null;
	},

	// Get a schema object by its type - if it is intended to be changed, it MUST be cloned first!
	getSchemaByType(typeref) {
		return Object.values(this._cache.value).find(schema => schema.type === typeref) ?? null;
	},

	// Get a schema or field object by its keyspec - if it is intended to be changed, it MUST be cloned first!
	getByKeyspec(keyspec) {
		if (!keyspec.includes('.')) {
			return this.getSchemaByType(keyspec);
		}
		const [ typeref, fieldref ] = keyspec.split('.');
		const schema = this.getSchemaByType(typeref);
		if (!schema)
			return null;
		const field = schema.fields.find(field => field.field === fieldref);
		return field ?? null;
	},

	// Get the underlying cache ref for watching purposes only! Note we only support this for watching the schema list, not the field list.
	getCacheRef() {
		return this._cache;
	},
};

function getDatagridColumnsFromSchema(schema, columnDefaults, from_row_editor = false, recursion_depth = 0) {
	console.log("Getting datagrid columns for schema " + schema?.id ?? 0 + " at recursion depth " + recursion_depth);
	let columns = schema ? schema?.fields ?? [] : [];
	// Generate columns from the schema fields first directly.
	columns = columns.map(c => ({
		'_field': c,
		dataField: c.field,
		dataType: c.getDxDataType(),
		key: c.field,
		name: c.field,
		caption: c.label,
		visible: columnDefaults[c.field]?.visible ?? (c.field.fieldtype == 'relationship' ? false : true),
		sortOrder: columnDefaults[c.field]?.sortOrder ?? undefined,
		width: columnDefaults[c.field]?.width ?? undefined,
		groupIndex: columnDefaults[c.field]?.groupIndex ?? undefined,
		allowEditing: c.field != 'id',
		editorOptions: c.getDxEditorOptions(),
		editorType: c.getDxDataType(true),
		editorFormType: null,
		editorFormOptions: null,
		minWidth: 20,			// Fixed min-width so it doesn't shrink too much and cause confusion when we can't see column headers existing.
	}));
	for (const col of columns) {
		// We never allow text areas in the datagrid column, and always override it. We still store the originals for use in popup forms where they are needed.
		if (col.editorType == 'dxTextArea') {
			col.editorFormType = col.editorType;
			col.editorFormOptions = col.editorOptions;
			console.log("Overriding editor type for datagrid column (" + (from_row_editor ? "row editor" : "standard") + "):", col);
			col.editorType = 'dxTextBox';
			col.editorOptions = col['_field'].getDxEditorOptions('text');
		}
		
		// Handling for text fields with restricted values becoming select boxes.
		if (col['_field'].fieldtype == 'text' && col['_field'].spec.restricted?.enabled && col['_field'].spec.restricted?.values) {
			col.editorType = 'dxSelectBox';
			col.editorOptions = {
				items: col['_field'].spec.restricted?.values,
				showClearButton: true,
			};
		}
		// Attach custom callbacks and data for special field types.
		if (col['_field'].fieldtype == 'attachment') {
			col.customizeText = (cellInfo) => {
				return cellInfo.value ? cellInfo.value.filename : "";
			}
			col.editCellTemplate = 'columnFileEdit';
			col.cellTemplate = 'columnFileRender';
		} else if (col['_field'].fieldtype == 'relationship') {
			col.customizeText = (cellInfo) => {
				if (!cellInfo.value || !Array.isArray(cellInfo.value))
					return "";
				return cellInfo.value.map(m => m.title ? m.title : (m.id ? `#${m.id}` : "#???")).join(", ");
			}
			col.editCellTemplate = 'columnReferenceEdit';
			col.cellTemplate = 'columnReferenceRender';
			col['_relationshipDetails'] = col['_field'].getRelationshipDetails();
		} else if (col['_field'].fieldtype == 'currency') {
			col.customizeText = (cellInfo) => {
				return cellInfo.value || cellInfo.value === 0 ? cellInfo.value.toLocaleString('en-US', {style: 'currency', currency: 'EUR' }) : "";
				// return cellInfo.value && cellInfo.value.amount ? cellInfo.value.amount.toLocaleString('en-US', {style: 'currency', currency: cellInfo.value.currency}) : "";
			}
		}
	}

	// Then replace relationship fields with expanded relationship objects, if possible.
	/*
	columns = columns.map(c => {
		if (c['_field'].fieldtype != 'relationship') {
			return c;
		}
		// Don't allow more than 1 level of relationship fields to be expanded, to avoid infinite loops. (NB: for more than one level, then flat() below might not work!)
		if (recursion_depth >= 1) {
			return null;
		}
		
		let relationDetails = c['_field'].getRelationshipDetails();
		if (!relationDetails) {
			console.warn(`Error getting relationship details for field ${c['_field'].keyspec}, skipping column.`);
			return null;
		}
		if (relationDetails.expectManyOpposites) {
			// If we are expecting many different opposite items, then we don't want to show a column here. Only for 1-1 or many-1 relations does this make sense.
			console.log("Relation is ignored because we are expecting many opposites:", relationDetails);
			return null;
		}
		let subColumns = relationDetails.oppositeSchemas.map(oppositeSchema => {
			return getDatagridColumnsFromSchema(oppositeSchema, columnDefaults, from_row_editor, recursion_depth + 1);
		});
		
		// We now have a selection of columns on the opposite schema. Note that this is potentially all columns from 2 schemas!
		// TODO: For now, we do not deal with this correctly.
		if (relationDetails.oppositeSchemas.length > 1) {
			console.warn(`Multiple opposite schemas found for relationship ${c['_field'].keyspec}, skipping column.`);
			return null;
		}
		let mergedColumns = subColumns.flat();
		let hasVisibleColumns = false;
		for (const col of mergedColumns) {
			// TODO: Refactor this so we are considering this up above. We shouldn't be doing this here!
			col.dataField = c['_field'].field + '.' + col['_field'].field;
			col.key = col.dataField;
			col.name = col.dataField;
			col.caption = col['_field'].label;
			col.visible = columnDefaults[col.dataField]?.visible ?? false;
			if (col.visible)
				hasVisibleColumns = true;
			col.sortOrder = columnDefaults[c.dataField]?.sortOrder ?? undefined;
			col.width = columnDefaults[c.dataField]?.width ?? undefined;
			col.groupIndex = columnDefaults[c.dataField]?.groupIndex ?? undefined;
			col.allowEditing = col['_field'].field != 'id';
		}

		const flatReferenceColumn = c;
		console.log('Flat reference column:', flatReferenceColumn, c);
		
		const bandedColumn = {
			caption: c['_field'].label,
			name: c['_field'].field,
			key: c['_field'].field + '.',		// We need an extra dot since it's possible to have this field used as a 'flat column' showing relationships.
			columns: mergedColumns,
			visible: hasVisibleColumns,
		};
		return [ bandedColumn, flatReferenceColumn ];
	}).flat().filter(Boolean);
	*/

	return columns;
}

export { SchemaCache, getDatagridColumnsFromSchema };